/** PDP colour-family swatches: fetch + morph `main` (plain links remain for no-JS / middle-click). */
import { morph } from '@theme/morph';
import VariantPicker from '@theme/variant-picker';
import { getViewParameterValue } from '@theme/utilities';

/** @type {AbortController | undefined} */
let abortController;

/**
 * @param {string} url
 * @returns {string}
 */
function buildRequestUrl(url) {
  const next = new URL(url, window.location.origin);
  const viewParam = getViewParameterValue();
  if (viewParam) next.searchParams.set('view', viewParam);
  return next.pathname + next.search;
}

/**
 * @param {string} requestUrl
 * @param {string} historyUrl
 */
async function navigateToColour(requestUrl, historyUrl) {
  abortController?.abort();
  abortController = new AbortController();

  const main = document.querySelector('main');
  if (!main) {
    window.location.assign(historyUrl);
    return;
  }

  main.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(requestUrl, { signal: abortController.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    let html = new DOMParser().parseFromString(await response.text(), 'text/html');
    let newMain = html.querySelector('main');

    if (!newMain) throw new Error('No main in colour product response');

    // Each colour is its own product and the swatch link carries no variant, so the page comes back with
    // its default size. If the shopper had picked a size, carry it over when the new colour has it in stock,
    // else take that colour's first in-stock size (all sold out: leave it, so Notify Me still works).
    // Ask the server for that variant before swapping, so the size, price and button are already right
    // on the first painted frame instead of correcting themselves afterwards.
    const sizeSelector = 'fieldset[data-is-size-option="true"] input[type="radio"]';
    const selectedSize = main.querySelector(`${sizeSelector}:checked`)?.value;
    let historyTarget = historyUrl;

    if (selectedSize) {
      const inStock = Array.from(newMain.querySelectorAll(sizeSelector)).filter(
        (input) => input.dataset.optionAvailable !== 'false'
      );
      const match = inStock.find((input) => input.value === selectedSize) ?? inStock[0];
      const variantId = match?.dataset.variantId;

      if (variantId) {
        const variantUrl = new URL(requestUrl, window.location.origin);
        variantUrl.searchParams.set('variant', variantId);
        const variantResponse = await fetch(variantUrl.pathname + variantUrl.search, {
          signal: abortController.signal,
        });
        if (variantResponse.ok) {
          const variantHtml = new DOMParser().parseFromString(await variantResponse.text(), 'text/html');
          const variantMain = variantHtml.querySelector('main');
          if (variantMain) {
            html = variantHtml;
            newMain = variantMain;
            const target = new URL(historyUrl, window.location.origin);
            target.searchParams.set('variant', variantId);
            historyTarget = target.pathname + target.search;
          }
        }
      }
    }

    const servedSize = newMain.querySelector(`${sizeSelector}:checked`)?.value;

    VariantPicker.closeOpenSizeGuideDialogs(main);
    morph(main, newMain);

    // morph() never clears `checked` on a radio the shopper has clicked, so the old size stays selected
    // beside the new one; make the swapped picker match what the server rendered.
    main.querySelectorAll(sizeSelector).forEach((input) => {
      const isServed = input.value === servedSize;
      input.checked = isServed;
      input.toggleAttribute('checked', isServed);
      input.dataset.currentChecked = String(isServed);
    });

    if (html.title) document.title = html.title;

    const nextUrl = new URL(historyTarget, window.location.origin);
    if (nextUrl.href !== window.location.href) {
      history.replaceState({}, '', nextUrl.pathname + nextUrl.search);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;
    console.error('Colour swatch navigation failed:', error);
    window.location.assign(historyUrl);
  } finally {
    main.removeAttribute('aria-busy');
  }
}

/**
 * @param {MouseEvent} event
 */
function onDocumentClick(event) {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('[data-color-swatch-nav] a.color-swatch__link');
  if (!(link instanceof HTMLAnchorElement)) return;

  if (!link.closest('[data-color-swatch-nav]')) return;
  if (link.classList.contains('is-selected') || link.getAttribute('aria-current') === 'true') {
    event.preventDefault();
    return;
  }

  const href = link.getAttribute('href');
  if (!href || href.startsWith('#')) return;

  const absolute = new URL(href, window.location.origin);
  if (absolute.origin !== window.location.origin) return;

  event.preventDefault();
  navigateToColour(buildRequestUrl(absolute.href), absolute.pathname + absolute.search);
}

document.addEventListener('click', onDocumentClick);
